import { FormArray, FormControl, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { inject } from '@angular/core';
import { CURRENT_USER_TOKEN, SharedUserService } from '@sbd-doug-frontend/shared/lib/types/current-user';
import {
  CIPXQuoteRequestIntakeForm,
  CIPXQuoteRequestIntakeFormPrincipalSelectionStep,
  CIPXQuoteRequestIntakeFormBondLookupStep,
  CIPXQuoteRequestIntakeFormRequestDetailsStep,
  CIPXQuoteRequestIntakeFormOwner,
  CIPXQuoteRequestIntakeFormIntakeFormAdditionalQuestions,
  CIPXQuoteRequestIntakeFormPrincipalSelectionType,
  CIPXQuoteRequestIntakeFormCommunications,
  CIPXQuoteRequestIntakeFormCommunicationsContact,
} from './intake-form.types';
import { formationDateValidator } from './intake-form.helpers';


// Custom validator for autocomplete controls that accepts both string and object values
function autocompleteRequiredValidator(control: AbstractControl): ValidationErrors | null {
  const value = control.value;

  // Consider valid if:
  // 1. It's an object (selected from dropdown)
  // 2. It's a non-empty string (typed by user)
  // 3. It's not null/undefined
  if (value && (typeof value === 'object' || (typeof value === 'string' && value.trim().length > 0))) {
    return null; // Valid
  }

  return { required: true }; // Invalid
}

export function createQuoteRequestForm() {
  const currentUser = inject<SharedUserService | null>(CURRENT_USER_TOKEN, { optional: true }) as SharedUserService | null;
  const defaultPrincipalSelection: CIPXQuoteRequestIntakeFormPrincipalSelectionType | null = currentUser?.current?.() ? CIPXQuoteRequestIntakeFormPrincipalSelectionType.Myself : CIPXQuoteRequestIntakeFormPrincipalSelectionType.New;

  // For new principal, user_id should be null; for myself, it should be set to current user
  const defaultUserId = defaultPrincipalSelection === CIPXQuoteRequestIntakeFormPrincipalSelectionType.Myself ? (currentUser?.current?.()?.principal_user_id ?? null) : null;

  const form = new FormGroup<CIPXQuoteRequestIntakeForm>({
    // Principal Selection Step FormGroup
    principalSelection: new FormGroup<CIPXQuoteRequestIntakeFormPrincipalSelectionStep>({
      principalSelectionType: new FormControl<CIPXQuoteRequestIntakeFormPrincipalSelectionType | null>(defaultPrincipalSelection as CIPXQuoteRequestIntakeFormPrincipalSelectionType | null),
      existingPrincipal: new FormControl<string | null>('', [
        (control: AbstractControl): ValidationErrors | null => {
          const parent = control.parent as FormGroup | null;
          if (!parent) {
            return null;
          }
          const selectionType = parent.get('principalSelectionType')?.value;
          const value = control.value;
          if (selectionType === CIPXQuoteRequestIntakeFormPrincipalSelectionType.Existing) {
            // require a GenericType-like object with value (id) OR a non-empty string matching one of the options could be acceptable later
            if (value && typeof value === 'object' && (value as {value?: unknown}).value) {
              return null;
            }
            return { required: true };
          }
          return null;
        }
      ]),
    }),
    // Bond Lookup Step FormGroup
    bondLookup: new FormGroup<CIPXQuoteRequestIntakeFormBondLookupStep> ({
      stateSelection: new FormControl(null, autocompleteRequiredValidator),
      bondFamilySelection: new FormControl(null, autocompleteRequiredValidator),
      stateBondSelection: new FormControl(null, autocompleteRequiredValidator),
      selectedPenaltyOption: new FormControl(null),
      bond_amount: new FormControl(null, [
        Validators.required,
        Validators.min(1), // Changed from 0 to 1 since bond amounts should be positive
        (control: AbstractControl): ValidationErrors | null => {
          const value = control.value;
          if (value === null || value === undefined || value === '') {
            return null; // Let required validator handle empty values
          }

          // Convert string with commas to number for validation
          const numericValue = typeof value === 'string'
            ? parseFloat(value.replace(/,/g, ''))
            : value;

          if (isNaN(numericValue)) {
            return { pattern: true };
          }

          return null;
        }
      ]),
      company_type_id: new FormControl(3, Validators.required),
      bond_family_label: new FormControl(''),
      surety_bond_user_notes: new FormControl(''),
    }),

    // Request Details Step FormGroup
    requestDetails: new FormGroup<CIPXQuoteRequestIntakeFormRequestDetailsStep>({
      addCompanyAddress: new FormControl(false),
      company_number_owners: new FormControl(1, [
        Validators.required,
        Validators.min(1),
        Validators.max(5),
      ]),
      company_formation_date: new FormControl<string | null>(null, [Validators.required, formationDateValidator]),
      principal: new FormGroup({
        company_name: new FormControl('', [
          Validators.required,
          Validators.minLength(2),
          Validators.pattern('^(?=.*\\S.*\\S).*$'), // at least 2 non-whitespace chars
        ]),
        company_dba: new FormControl(''),
        company_address_street_1: new FormControl(''),
        company_address_street_2: new FormControl(''),
        company_address_city: new FormControl(''),
        company_address_state_abbreviation: new FormControl(''),
        company_address_zip: new FormControl(''),
        company_address_county: new FormControl(''),
        company_address_phone: new FormControl(''),
      }),
      owners: new FormArray<FormGroup<CIPXQuoteRequestIntakeFormOwner>>([]),
    }),

    // Communications Step FormGroup - unified contacts array
    communications: new FormGroup<CIPXQuoteRequestIntakeFormCommunications>({
      contacts: new FormArray<FormGroup<CIPXQuoteRequestIntakeFormCommunicationsContact>>([]),
      use_same_for_renewals: new FormControl<boolean>(true, { nonNullable: true }),
    }),

    // Shared/cross-step fields
    state_bond_id: new FormControl(null, Validators.required),
    requested_coverage_begin_date: new FormControl(null, Validators.required),
    additional_questions: new FormGroup<CIPXQuoteRequestIntakeFormIntakeFormAdditionalQuestions>({}),
    additional_comments: new FormControl(''),
    state_abbreviation: new FormControl(''),
    user_id: new FormControl(defaultUserId),
  });

  return form;
}
